/** Offline auth: validates against `usuarios` + RBAC in the same sql.js DB as the rest of the app. */
import type { Database } from 'sql.js';
import type { AuthProviderPort } from '../../domain/ports/AuthProviderPort.ts';
import type { AuthTokenStoragePort } from '../../domain/ports/AuthTokenStoragePort.ts';
import { hashLocalPassword } from '@src/modules/kernel/localPasswordHash.ts';

const LOCAL_TOKEN_PREFIX = 'local.';

export type GetLocalSqliteDatabase = () => Database | undefined;

/** Optional: last offline startup failure (telemetry / UX messages). */
export type LocalSqliteHealthQuery = () => { readonly lastError?: string } | undefined;

export class LocalSqlAuthAdapter implements AuthProviderPort {
  /** Constructs the local SQL auth adapter. */
  constructor(
    private readonly tokenStorage: AuthTokenStoragePort | null,
    private readonly getDb: GetLocalSqliteDatabase,
    private readonly getHealth?: LocalSqliteHealthQuery,
  ) {}
  /** Returns the database unavailable message. */
  private dbUnavailableMessage(): string {
    const hint = this.getHealth?.()?.lastError;
    if (hint) return `Base de datos local no disponible: ${hint}`;
    return 'Base de datos local no disponible. Compruebe que la app cargó /database/local.db.';
  }

  /** Authenticates a user against the local database. */
  async login(usernameOrEmail: string, password: string): Promise<{ accessToken: string; user?: unknown }> {
    const id = usernameOrEmail.trim();
    if (!id || !password) {
      throw new Error('Credenciales incorrectas');
    }

    const db = this.getDb();
    if (!db) {
      throw new Error(this.dbUnavailableMessage());
    }
    const row = this.findUsuarioRow(db, id);
    if (!row?.password_hash) {
      throw new Error('Credenciales incorrectas');
    }
    const hashed = await hashLocalPassword(password);
    if (hashed !== row.password_hash) {
      throw new Error('Credenciales incorrectas');
    }
    const user = this.buildShellUser(db, row.id);
    const token = `${LOCAL_TOKEN_PREFIX}${row.id}`;
    return { accessToken: token, user };
  }
  /** Logs out the current user. */
  async logout(): Promise<void> {
    this.tokenStorage?.clearToken();
  }

  /** Retrieves the current session. */
  async getSession(): Promise<{ accessToken: string | null; user?: unknown }> {
    const token = this.tokenStorage?.getToken() ?? null;
    const userId = this.localUserId(token);
    if (userId == null) return { accessToken: null, user: null };
    const db = this.getDb();
    return { accessToken: token, user: db ? this.buildShellUser(db, userId) : null };
  }

  /** Checks if the user is authenticated. */
  async isAuthenticated(): Promise<boolean> {
    return !!this.tokenStorage?.getToken()?.trim();
  }

  /** Retrieves the current user. */
  async getUser(): Promise<unknown> {
    const token = this.tokenStorage?.getToken() ?? null;
    const userId = this.localUserId(token);
    if (userId == null) return null;
    const db = this.getDb();
    return db ? this.buildShellUser(db, userId) : null;
  }

  /** Extracts the user ID from a local token. */
  private localUserId(token: string | null): number | null {
    if (!token?.trim() || !token.startsWith(LOCAL_TOKEN_PREFIX)) return null;
    const id = Number.parseInt(token.slice(LOCAL_TOKEN_PREFIX.length), 10);
    return Number.isFinite(id) ? id : null;
  }

  /** Finds a user row by email or username. */
  private findUsuarioRow(
    db: Database,
    login: string,
  ): { id: number; password_hash: string } | null {
    const norm = login.trim().toLowerCase();
    // Inclusion Social stores the login id (documento) in `nombre`; local seeds use `nombre_usuario`.
    const stmt = db.prepare(
      `SELECT id, password_hash FROM usuarios
       WHERE estado = 1 AND (lower(email) = ? OR lower(nombre_usuario) = ? OR lower(nombre) = ?) LIMIT 1`,
    );
    try {
      stmt.bind([norm, norm, norm]);
      if (!stmt.step()) return null;
      const o = stmt.getAsObject() as { id?: number; password_hash?: string };
      const id = typeof o.id === 'number' ? o.id : Number(o.id);
      const ph = typeof o.password_hash === 'string' ? o.password_hash : '';
      if (!Number.isFinite(id) || !ph) return null;
      return { id, password_hash: ph };
    } finally {
      stmt.free();
    }
  }
  
  /** Builds a shell user object from the database. */
  private buildShellUser(db: Database, usuarioId: number): Record<string, unknown> {
    const stmt = db.prepare(
      `SELECT id, public_id, nombre, nombre_usuario, email FROM usuarios WHERE id = ? AND estado = 1 LIMIT 1`,
    );
    let base: { id: number; public_id: string; nombre: string; nombre_usuario: string; email: string | null };
    try {
      stmt.bind([usuarioId]);
      if (!stmt.step()) {
        throw new Error('Usuario no encontrado');
      }
      const o = stmt.getAsObject();
      const publicId = typeof o.public_id === 'string' ? o.public_id : '';
      const nombreUsuario = typeof o.nombre_usuario === 'string' ? o.nombre_usuario : '';
      const nombrePersona = typeof o.nombre === 'string' ? o.nombre.trim() : '';
      const emailVal = o.email;
      base = {
        id: Number(o.id),
        public_id: publicId,
        nombre: nombrePersona,
        nombre_usuario: nombreUsuario,
        email: typeof emailVal === 'string' ? emailVal : null,
      };
    } finally {
      stmt.free();
    }

    const roleStmt = db.prepare(
      `SELECT r.public_id AS role_id FROM usuario_rol ur
       JOIN roles r ON r.id = ur.rol_id AND r.estado = 1
       WHERE ur.usuario_id = ? LIMIT 1`,
    );
    let role: string | undefined;
    try {
      roleStmt.bind([usuarioId]);
      if (roleStmt.step()) {
        const ro = roleStmt.getAsObject() as { role_id?: string };
        role = typeof ro.role_id === 'string' ? ro.role_id : undefined;
      }
    } finally {
      roleStmt.free();
    }

    return {
      id: String(base.id),
      public_id: base.public_id || undefined,
      email: base.email ?? undefined,
      nombre: base.nombre || undefined,
      nombre_usuario: base.nombre_usuario,
      role,
      permissions: this.loadUserPermissions(db, usuarioId),
    };
  }

  /** Permission codes granted via usuario_rol → rol_permiso → permisos. */
  private loadUserPermissions(db: Database, usuarioId: number): string[] {
    const stmt = db.prepare(
      `SELECT DISTINCT p.public_id AS code
       FROM usuario_rol ur
       JOIN roles r ON r.id = ur.rol_id AND r.estado = 1
       JOIN rol_permiso rp ON rp.rol_id = r.id
       JOIN permisos p ON p.id = rp.permiso_id AND p.estado = 1
       WHERE ur.usuario_id = ?
       ORDER BY p.id`,
    );
    try {
      stmt.bind([usuarioId]);
      const codes: string[] = [];
      while (stmt.step()) {
        const o = stmt.getAsObject() as { code?: string };
        if (typeof o.code === 'string' && o.code) codes.push(o.code);
      }
      return codes;
    } finally {
      stmt.free();
    }
  }
}
